package com.shadowings.gero.data.lessons

import com.shadowings.gero.presentation.model.Word

data class DirectGenitivePhrase(
    val hieroglyphs: String,
    val correctPhrase: String,
    val correctTokens: List<String>,
    val word1: Word,
    val word2: Word
)

data class IndirectGenitivePhrase(
    val hieroglyphs: String,
    val correctPhrase: String,
    val correctTokens: List<String>,
    val possessedWord: Word,
    val possessorWord: Word,
    val particleTransliteration: String = "n"
)

/**
 * Splits a fully-assembled, already-localized/inflected phrase into the individual word-bank
 * tokens a "Build the sentence" exercise expects the player to pick. This must be the ONLY way
 * correct tiles are derived for genitive phrases: deriving tokens independently from raw word
 * data (re-reading article/noun/genitive fields) can drift out of sync with whatever per-language
 * inflection (e.g. German genitive noun endings, see formatGermanGenitivePhrase in SentenceFormatting) was applied
 * when correctPhrase was built, leaving a required inflected tile (e.g. "Vaters") missing from the
 * word bank entirely. Mirrors the tokenizer already duplicated in WordSelectionComposable.kt,
 * VsoSentenceUtils.kt and PseudoverbalSentenceUtils.kt.
 */
// Also reused (internal, not private) to split any raw distractor word field — e.g.
// getMeaning()/getGenitivo() — into individual tiles: some vocabulary entries have a
// multi-word meaning (e.g. "König von Oberägypten", "Re dell'alto Egitto") or a multi-word
// genitive article (Spanish "de la"/"de los"), and a distractor built from the raw field
// value without this split would show up as a single glued tile instead of one-word-per-tile.
internal fun tokenizePhrase(phrase: String): List<String> {
    return phrase
        .replace("’", "'")
        .replace("'", "' ")
        .replace(" ,", ",")
        .trim()
        .split(Regex("\\s+"))
        .flatMap { token ->
            when {
                token.isBlank() -> emptyList()
                token.endsWith(",") && token.length > 1 -> listOf(token.dropLast(1), ",")
                else -> listOf(token)
            }
        }
}

/**
 * Cleans the meaning field for phrase assembly. German capitalizes all nouns,
 * so unlike Italian/English it must not be forced to lowercase.
 */
private fun cleanNoun(languageCode: String, word: Word): String =
    word.inlineMeaning(languageCode, word.getMeaning(languageCode).split("/")[0].split(",")[0].trim())

fun extractParticleTransliteration(connector: String): String {
    if (!connector.contains("N35")) return "n"
    val sb = StringBuilder("n")
    if (connector.contains("M17")) {
        sb.append("y")
    }
    if (connector.contains("Z7") || connector.contains("G43")) {
        sb.append("w")
    }
    if (connector.contains("X1")) {
        sb.append("t")
    }
    return sb.toString()
}

/**
 * Builds indirect genitive phrases from explicit triples.
 * Each triple is (possessedId, connector, possessorId) where connector is already
 * formatted with the proper MdC separators (e.g. "-N35-").
 */
fun calculateIndirectGenitivePhrases(
    words: List<Word>,
    triples: List<Triple<String, String, String>>,
    languageCode: String
): List<IndirectGenitivePhrase> {
    val wordMap = words.associateBy { it.id.uppercase() }

    return triples.mapNotNull { (possessedId, connector, possessorId) ->
        val possessed = wordMap[possessedId.uppercase()] ?: return@mapNotNull null
        val possessor = wordMap[possessorId.uppercase()] ?: return@mapNotNull null

        val hieroglyphs = "${possessed.gardiner}${connector}${possessor.gardiner}"
        val particleTransliteration = extractParticleTransliteration(connector)

        val art = possessed.getArticolo(languageCode)
        val noun1 = cleanNoun(languageCode, possessed)
        val gen = possessor.getGenitivo(languageCode)
        val noun2 = cleanNoun(languageCode, possessor)

        val part1 = if (!art.isNullOrBlank() && art != "-") {
            if (art.endsWith("'")) "$art$noun1" else "$art $noun1"
        } else {
            noun1
        }
        val part2 = if (!gen.isNullOrBlank() && gen != "-") {
            if (languageCode == "de") {
                formatGermanGenitivePhrase(gen, noun2, possessor)
            } else if (gen.endsWith("'")) {
                "$gen$noun2"
            } else {
                "$gen $noun2"
            }
        } else {
            noun2
        }

        val correctPhrase = "$part1 $part2"

        IndirectGenitivePhrase(
            hieroglyphs = hieroglyphs,
            correctPhrase = correctPhrase,
            correctTokens = tokenizePhrase(correctPhrase),
            possessedWord = possessed,
            possessorWord = possessor,
            particleTransliteration = particleTransliteration
        )
    }
}

fun calculateDirectGenitivePhrases(
    words: List<Word>,
    pairs: List<Pair<String, String>>,
    takeCount: Int = 15,
    languageCode: String
): List<DirectGenitivePhrase> {
    val wordMap = words.associateBy { it.id.uppercase() }
    val availablePairs = pairs.mapNotNull { (id1, id2) ->
        val w1 = wordMap[id1]
        val w2 = wordMap[id2]
        if (w1 != null && w2 != null) Pair(w1, w2) else null
    }.shuffled().take(takeCount)

    return availablePairs.map { (word1, word2) ->
        val art1 = word1.getArticolo(languageCode)
        val noun1 = cleanNoun(languageCode, word1)
        val gen2 = word2.getGenitivo(languageCode)
        val noun2 = cleanNoun(languageCode, word2)

        val part1 = if (!art1.isNullOrBlank() && art1 != "-") {
            if (art1.endsWith("'")) "$art1$noun1" else "$art1 $noun1"
        } else {
            noun1
        }
        val part2 = if (!gen2.isNullOrBlank() && gen2 != "-") {
            if (languageCode == "de") {
                formatGermanGenitivePhrase(gen2, noun2, word2)
            } else if (gen2.endsWith("'")) {
                "$gen2$noun2"
            } else {
                "$gen2 $noun2"
            }
        } else {
            noun2
        }
        val correctPhrase = "$part1 $part2"
        val hieroglyphs = "${word1.gardiner}-${word2.gardiner}"

        DirectGenitivePhrase(hieroglyphs, correctPhrase, tokenizePhrase(correctPhrase), word1, word2)
    }
}
